import { api, tokenStorage } from '../lib/api';
import { User } from '../types';

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface RegisterResponse {
  message: string;
  email: string;
}

export interface MyWorkspaceSummary {
  workspace_id: string;
  workspace_name: string;
  role: string;
  group_id: string | null;
  group_ids?: string[];
  is_active: boolean;
}

export interface InvitationPreview {
  id: string;
  workspace_name: string | null;
  role: string;
  email: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
}

export interface ProjectInvitationPreview {
  id: string;
  project_name: string | null;
  workspace_name: string | null;
  email: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expired: boolean;
  account_exists: boolean;
}

export const authService = {
  async login(email: string, password: string): Promise<User> {
    const { data } = await api.post<{ success: boolean; data: LoginResponse }>('/auth/login', {
      email,
      password
    });
    tokenStorage.setTokens(data.data.accessToken, data.data.refreshToken);
    return data.data.user;
  },

  // Self-serve signup. Does NOT return tokens — the account can't be used
  // until the verification email is confirmed (see verifyEmail below).
  async register(
    email: string,
    name: string,
    password: string,
    workspaceName: string,
    turnstileToken: string,
    timezone?: string
  ): Promise<RegisterResponse> {
    const { data } = await api.post<{ success: boolean; data: RegisterResponse }>('/auth/register', {
      email,
      name,
      password,
      workspaceName,
      timezone,
      turnstileToken
    });
    return data.data;
  },

  // "Sign in with Google" — idToken comes from Google Identity Services'
  // credential callback (see Login.tsx/Register.tsx). Covers both login
  // and first-time sign-up in one call, so it returns tokens immediately
  // like login() does (no email-verification step, Google already proved
  // ownership) — see AuthService.googleLogin on the backend.
  async googleLogin(idToken: string): Promise<User> {
    const { data } = await api.post<{ success: boolean; data: LoginResponse }>('/auth/google', {
      idToken
    });
    tokenStorage.setTokens(data.data.accessToken, data.data.refreshToken);
    return data.data.user;
  },

  async acceptInvite(token: string, name: string, password: string): Promise<User> {
    const { data } = await api.post<{ success: boolean; data: LoginResponse }>('/auth/accept-invite', {
      token,
      name,
      password
    });
    tokenStorage.setTokens(data.data.accessToken, data.data.refreshToken);
    return data.data.user;
  },

  async acceptProjectInvite(token: string, name: string, password: string): Promise<User> {
    const { data } = await api.post<{ success: boolean; data: LoginResponse }>('/auth/accept-project-invite', {
      token,
      name,
      password
    });
    tokenStorage.setTokens(data.data.accessToken, data.data.refreshToken);
    return data.data.user;
  },

  async verifyEmail(token: string): Promise<User> {
    const { data } = await api.post<{ success: boolean; data: LoginResponse }>('/auth/verify-email', {
      token
    });
    tokenStorage.setTokens(data.data.accessToken, data.data.refreshToken);
    return data.data.user;
  },

  async resendVerification(email: string): Promise<{ message: string }> {
    const { data } = await api.post<{ success: boolean; data: { message: string } }>('/auth/resend-verification', {
      email
    });
    return data.data;
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const { data } = await api.post<{ success: boolean; data: { message: string } }>('/auth/forgot-password', {
      email
    });
    return data.data;
  },

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const { data } = await api.post<{ success: boolean; data: { message: string } }>('/auth/reset-password', {
      token,
      password
    });
    return data.data;
  },

  async getMe(): Promise<User> {
    const { data } = await api.get<{ success: boolean; data: User }>('/auth/me');
    return data.data;
  },

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      tokenStorage.clearTokens();
    }
  },

  // All workspaces the caller is a member of (up to 3), with which one is
  // currently active — the switcher UI's data source.
  async listMyWorkspaces(): Promise<MyWorkspaceSummary[]> {
    const { data } = await api.get<{ success: boolean; data: MyWorkspaceSummary[] }>('/auth/my-workspaces');
    return data.data;
  },

  // Flips which workspace is active for this session. Takes effect on the
  // very next request — no new token needed (see backend
  // AuthService.switchActiveWorkspace).
  async switchWorkspace(workspaceId: string): Promise<User> {
    const { data } = await api.post<{ success: boolean; data: { user: User } }>('/auth/switch-workspace', {
      workspace_id: workspaceId
    });
    return data.data.user;
  },

  // Public preview of a workspace invitation — no auth needed, used by the
  // accept-invite page before the visitor does anything.
  async getInvitationPreview(token: string): Promise<InvitationPreview> {
    const { data } = await api.get<{ success: boolean; data: InvitationPreview }>(`/auth/invitations/by-token/${token}`);
    return data.data;
  },

  // Public preview of a PROJECT invitation — tells the accept page whether the
  // invited email already has an account (log in and join) or not (signup form).
  async getProjectInvitationPreview(token: string): Promise<ProjectInvitationPreview> {
    const { data } = await api.get<{ success: boolean; data: ProjectInvitationPreview }>(`/auth/project-invitations/by-token/${token}`);
    return data.data;
  },

  // Logged-in account accepts a project invitation sent to its email; adds the
  // workspace membership too if needed. Does not switch the active workspace.
  async acceptProjectInviteExisting(token: string): Promise<{ project: { id: string; name: string }; workspace: { id: string; name: string } }> {
    const { data } = await api.post<{ success: boolean; data: { project: { id: string; name: string }; workspace: { id: string; name: string } } }>('/auth/accept-project-invite/existing', { token });
    return data.data;
  },

  // Authenticated counterpart to acceptInvite: adds a membership to an
  // ALREADY-logged-in account instead of creating a new one. Does not
  // switch the caller's active workspace.
  async joinWorkspace(invitationId: string): Promise<{ workspace: { id: string; name: string } }> {
    const { data } = await api.post<{ success: boolean; data: { workspace: { id: string; name: string } } }>('/auth/join-workspace', {
      invitationId
    });
    return data.data;
  }
};
